import React, { useState, useRef } from 'react';
import { UploadCloud, FileText, CheckCircle2, Sparkles, ShieldAlert, Tag, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { canonicalizeFormula } from '../lib/formulaCanonicalizer';
import { submitFormulaForReview } from '../lib/api';
import { EQUATION_BY_ID } from '../data/formulas';

interface ParsedPreviewQuestion {
  source: string;
  questionNumber: number;
  subject: string;
  weight: number;
  statement: string;
  theory: string;
  formulaBridge: string;
  canonicalFormulaId: string | null;
  confidence: 'EXACT' | 'UNRESOLVED';
  reviewReason?: string;
  descriptorCode?: string;
}

export const UploadPage: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState<boolean>(false);
  const [parsedQuestions, setParsedQuestions] = useState<ParsedPreviewQuestion[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        setSelectedFile(file);
        setStatusMessage(null);
      } else {
        setStatusMessage('Por favor, selecione um arquivo PDF de prova válido.');
      }
    }
  };

  const handleSimulateParse = async () => {
    if (!selectedFile) return;

    setParsing(true);
    setStatusMessage('Lendo PDF, canonicalizando fórmulas e verificando correspondência com o prontuário...');

    try {
      const fileName = selectedFile.name.replace(/\.[^/.]+$/, '');

      // Sample questions illustrating both exact canonical match and unresolved review queue routing
      const rawCandidates = [
        {
          source: fileName,
          questionNumber: 16,
          subject: 'MATEMATICA',
          weight: 5,
          statement:
            'Um agricultor projeta uma estufa cuja seção transversal é uma parábola de equação $y = -0{,}2x^2 + 2x$. Determine a altura máxima da estufa em metros.',
          theory: 'Função Quadrática - Vértice (Yv)',
          formulaBridge: 'Y_v = -\\Delta / (4a)',
        },
        {
          source: fileName,
          questionNumber: 26,
          subject: 'FISICA',
          weight: 2,
          statement:
            'Uma esfera condutora é solta do repouso no vácuo de uma altura de $20\\text{ m}$. Calcule a velocidade de impacto no solo considerando $g = 10\\text{ m/s}^2$.',
          theory: 'Cinemática / Conservação de Energia',
          formulaBridge: 'v^2 = v_0^2 + 2g\\Delta h',
        },
        {
          source: fileName,
          questionNumber: 34,
          subject: 'FISICA',
          weight: 2,
          statement:
            'Um gás ideal monoatômico sofre uma transformação termodinâmica onde $P \\cdot V = n R T$. Determine a temperatura no estado de equilíbrio.',
          theory: 'Termodinâmica - Gases Ideais (Não catalogada no edital IFF)',
          formulaBridge: 'P \\cdot V = n R T',
        },
      ];

      const processed: ParsedPreviewQuestion[] = [];

      for (const item of rawCandidates) {
        const canonical = canonicalizeFormula(item.formulaBridge);
        let descCode: string | undefined = undefined;

        if (canonical.confidence === 'EXACT' && canonical.formulaId) {
          descCode = EQUATION_BY_ID[canonical.formulaId]?.descriptorCode;
        } else {
          // If unresolvable, automatically route to the Review Queue
          await submitFormulaForReview({
            caderno: item.source,
            questionNumber: item.questionNumber,
            statement: item.statement,
            subject: item.subject,
            extractedRawFormula: item.formulaBridge,
            canonicalFormulaAttempt: canonical.normalizedString,
            reason: canonical.reason || 'Fórmula não catalogada no prontuário oficial',
          });
        }

        processed.push({
          ...item,
          canonicalFormulaId: canonical.formulaId,
          confidence: canonical.confidence,
          reviewReason: canonical.reason,
          descriptorCode: descCode,
        });
      }

      setParsedQuestions(processed);
      const exactCount = processed.filter((p) => p.confidence === 'EXACT').length;
      const unresCount = processed.filter((p) => p.confidence === 'UNRESOLVED').length;

      setStatusMessage(
        `PDF processado com sucesso! ${exactCount} questões associadas ao prontuário e ${unresCount} questão(ões) enviada(s) para a Fila de Curadoria Humana (zero suposições incorretas).`
      );
    } catch {
      setStatusMessage('Erro ao analisar o arquivo PDF.');
    } finally {
      setParsing(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-paper-50 px-4 py-8 dark:bg-study-slate sm:px-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <div>
          <div className="flex items-center gap-2">
            <UploadCloud className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            <h1 className="font-serif text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
              Upload & Ingestão de Cadernos de Prova
            </h1>
          </div>
          <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
            Pipeline de extração taxonômica e canonicalização de fórmulas com separação estrita: fórmulas oficiais são associadas deterministicamente por ID; fórmulas não catalogadas entram na fila de curadoria humana.
          </p>
        </div>

        {/* Upload Drop Zone */}
        <div
          onClick={() => fileInputRef.current?.click()}
          className="cursor-pointer rounded-xl border-2 border-dashed border-paper-300 bg-paper-100 p-8 text-center transition hover:border-zinc-500 hover:bg-paper-200/50 dark:border-study-line dark:bg-study-panel dark:hover:border-zinc-500 dark:hover:bg-study-card"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            className="hidden"
          />
          <FileText className="mx-auto h-8 w-8 text-zinc-400 mb-2" />
          <p className="text-xs font-semibold text-zinc-800 dark:text-zinc-200">
            {selectedFile ? selectedFile.name : 'Clique ou arraste um caderno de prova do IFF em PDF aqui'}
          </p>
          <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1">
            Formatos suportados: Cadernos de Bacharelado e Tecnologia IFF
          </p>
        </div>

        {selectedFile && (
          <div className="flex items-center justify-between rounded-lg border border-paper-200 bg-paper-100 p-3.5 dark:border-study-line dark:bg-study-panel shadow-sm">
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-zinc-800 dark:text-zinc-200">{selectedFile.name}</span>
              <span className="font-mono text-[10px] text-zinc-500">
                ({Math.round(selectedFile.size / 1024)} KB)
              </span>
            </div>
            <button
              type="button"
              onClick={handleSimulateParse}
              disabled={parsing}
              className="flex items-center gap-1.5 rounded bg-zinc-900 px-4 py-2 text-xs font-bold text-white hover:bg-zinc-800 disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white transition"
            >
              <Sparkles className="h-3.5 w-3.5 text-amber-400" />
              {parsing ? 'Processando e Canonicalizando...' : 'Iniciar Extração & Canonicalização'}
            </button>
          </div>
        )}

        {statusMessage && (
          <div className="rounded-lg border border-paper-300 bg-paper-100 p-3.5 text-xs text-zinc-700 dark:border-study-line dark:bg-study-panel dark:text-zinc-300 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
            <span>{statusMessage}</span>
          </div>
        )}

        {/* Parsed Preview Section */}
        {parsedQuestions.length > 0 && (
          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between border-b border-paper-200 pb-2 dark:border-study-line">
              <h3 className="font-serif text-sm font-bold text-zinc-900 dark:text-zinc-100">
                Resultado do Tratamento Taxonômico & Ingestão
              </h3>
              <button
                type="button"
                onClick={() => navigate('/grafo')}
                className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
              >
                <span>Ver Fila de Curadoria</span>
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>

            {parsedQuestions.map((q, idx) => (
              <div
                key={idx}
                className={`rounded-xl border p-5 transition shadow-sm space-y-3 ${
                  q.confidence === 'EXACT'
                    ? 'border-emerald-300 bg-paper-100 dark:border-emerald-800/80 dark:bg-study-panel'
                    : 'border-amber-300 bg-amber-50/40 dark:border-amber-800/80 dark:bg-study-panel'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-paper-200 pb-2.5 dark:border-study-line">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold uppercase px-2 py-0.5 rounded border border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300">
                      {q.subject} (Peso {q.weight})
                    </span>
                    <span className="font-mono text-xs text-zinc-500">
                      Questão {q.questionNumber}
                    </span>
                  </div>

                  {q.confidence === 'EXACT' ? (
                    <span className="flex items-center gap-1 font-mono text-[11px] font-bold text-emerald-700 dark:text-emerald-400 rounded bg-emerald-100 px-2 py-0.5 dark:bg-emerald-950 border border-emerald-300 dark:border-emerald-800">
                      <Tag className="h-3 w-3" />
                      ID Canônico: {q.canonicalFormulaId}
                      {q.descriptorCode ? ` (${q.descriptorCode})` : ''}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 font-mono text-[11px] font-bold text-amber-800 dark:text-amber-300 rounded bg-amber-100 px-2 py-0.5 dark:bg-amber-950 border border-amber-300 dark:border-amber-800">
                      <ShieldAlert className="h-3.5 w-3.5 text-amber-600" />
                      Enviada para Fila de Revisão Humana
                    </span>
                  )}
                </div>

                <div className="text-xs text-zinc-800 dark:text-zinc-200 prose prose-zinc dark:prose-invert max-w-none leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                    {q.statement}
                  </ReactMarkdown>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs border-t border-paper-200 pt-3 dark:border-study-line">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-zinc-400 block">Teoria do Enunciado</span>
                    <p className="font-medium text-zinc-800 dark:text-zinc-200">{q.theory}</p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-zinc-400 block">Fórmula Bruta Extraída</span>
                    <p className="font-mono text-indigo-700 dark:text-indigo-400">{q.formulaBridge}</p>
                  </div>
                </div>

                {q.confidence === 'UNRESOLVED' && q.reviewReason && (
                  <div className="rounded bg-amber-100/70 p-2 text-[11px] text-amber-900 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-900">
                    <strong>Motivo da Curadoria:</strong> {q.reviewReason}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
